import { Link } from 'react-router';
import type { LucideIcon } from 'lucide-react';
import { CalendarDays, FileCheck2, GraduationCap, Library, ListChecks } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';

const HIGHLIGHTS: { icon: LucideIcon; title: string; text: string }[] = [
  { icon: ListChecks, title: 'Plano por orientando', text: 'Atividades, comprovações e o prazo do título de cada um.' },
  { icon: CalendarDays, title: 'Agenda única', text: 'O que vence, de todos os orientandos, numa só tela.' },
  { icon: Library, title: 'Modelos prontos', text: 'Lançam as atividades até a defesa de uma vez.' },
  { icon: FileCheck2, title: 'Avaliação em um clique', text: 'Aprove ou peça ajustes e baixe todo o material em ZIP.' },
];

const FACTS = [
  'Cadastro em menos de um minuto',
  'Entre com e-mail ou Google',
  'Orientandos entram por convite',
] as const;

/** Campo da entrada: mais alto, cantos arredondados e ícone à esquerda (como no votaDCR). */
export function WelcomeInput({ icon: Icon, className, ...props }: React.ComponentProps<typeof Input> & { icon: LucideIcon }) {
  return (
    <div className="relative">
      <Icon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2" aria-hidden />
      <Input className={cn('bg-muted/50 focus-visible:bg-card h-12 rounded-xl pl-10', className)} {...props} />
    </div>
  );
}

/** Botão principal da entrada: alto, em negrito e com sombra na cor principal. */
export const welcomeSubmitClass = 'mt-1 h-12 rounded-xl text-base font-semibold shadow-lg shadow-primary/25';

/** Marca do Orienta: capelo num quadrado arredondado. */
function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex size-9 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25">
        <GraduationCap className="size-5" aria-hidden />
      </span>
      <span className="text-xl font-bold tracking-tight">Orienta</span>
    </div>
  );
}

/** Prévia ilustrativa do produto (decorativa): o cartão de um orientando com avisos flutuando sobre ele. */
function ProductPreview() {
  return (
    <div aria-hidden className="relative w-full max-w-md pt-9 pr-6 pl-4">
      <div className="bg-card text-card-foreground rounded-2xl p-5 shadow-2xl shadow-black/30 ring-1 ring-black/5">
        <div className="flex items-center gap-3">
          <span className="bg-accent text-accent-foreground flex size-10 items-center justify-center rounded-full text-sm font-semibold">
            BS
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold">Bruno Souza</p>
            <p className="text-muted-foreground text-xs">Mestrado · ingresso em mar/2026</p>
          </div>
          <span className="bg-success/15 rounded-md px-2 py-0.5 text-xs font-medium text-green-800 dark:text-success">
            No ritmo
          </span>
        </div>
        <div className="mt-4 flex items-baseline justify-between text-sm">
          <span className="text-muted-foreground">Qualificação</span>
          <span className="font-semibold">em 142 dias</span>
        </div>
        <div className="bg-muted mt-2 h-2 overflow-hidden rounded-full">
          <div className="bg-primary h-full w-[58%] rounded-full" />
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          {[
            ['5', 'concluídas'],
            ['2', 'para avaliar'],
            ['0', 'atrasadas'],
          ].map(([n, label]) => (
            <div key={label} className="bg-muted rounded-lg py-2">
              <p className="text-lg font-bold">{n}</p>
              <p className="text-muted-foreground text-[11px]">{label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card text-card-foreground absolute top-0 right-0 flex items-center gap-2.5 rounded-xl px-3 py-2 shadow-xl shadow-black/25 ring-1 ring-black/5">
        <span className="bg-info/15 text-info flex size-7 items-center justify-center rounded-lg">
          <FileCheck2 className="size-4" />
        </span>
        <span className="text-xs leading-tight">
          <span className="block font-semibold">Capítulo 2 enviado</span>
          <span className="text-muted-foreground">Ana Lima · aguardando avaliação</span>
        </span>
      </div>

      <div className="bg-card text-card-foreground absolute -bottom-5 left-0 flex items-center gap-2.5 rounded-xl px-3 py-2 shadow-xl shadow-black/25 ring-1 ring-black/5">
        <span className="bg-accent text-accent-foreground flex size-7 items-center justify-center rounded-lg">
          <CalendarDays className="size-4" />
        </span>
        <span className="text-xs leading-tight">
          <span className="block font-semibold">3 entregas nesta semana</span>
          <span className="text-muted-foreground">na agenda de todos os orientandos</span>
        </span>
      </div>
    </div>
  );
}

/**
 * Entrada da plataforma (login e cadastro): à esquerda, a vitrine do que o Orienta entrega; à direita, o formulário.
 * No celular, a marca e a proposta ficam numa faixa curta acima do formulário.
 */
export function WelcomeLayout({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-card flex min-h-svh flex-col lg:flex-row">
      <section className="welcome-hero text-topbar-foreground relative isolate overflow-hidden px-6 pt-6 pb-8 sm:px-10 lg:flex lg:w-[52%] lg:flex-col lg:px-14 lg:py-10 xl:w-[56%] xl:px-20">
        {/* Decoração: grade de pontos tênue que some para baixo, um brilho claro no canto (longe do texto) e uma sombra no canto oposto. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 [mask-image:linear-gradient(to_bottom,black,transparent_85%)] bg-[radial-gradient(circle,rgb(255_255_255/0.12)_1px,transparent_1px)] [background-size:22px_22px]"
        />
        <div aria-hidden className="bg-hero-accent/25 pointer-events-none absolute -top-40 -right-32 -z-10 size-[26rem] rounded-full blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-40 -left-40 -z-10 size-[28rem] rounded-full bg-black/20 blur-3xl" />

        <Brand />

        <div className="mt-6 lg:my-auto lg:py-6">
          <div className="max-w-2xl motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 motion-safe:duration-700">
            <p className="hidden w-fit items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium ring-1 ring-white/20 sm:flex">
              <span aria-hidden className="bg-hero-accent size-2 rounded-full" />
              Para orientadores de iniciação científica, mestrado e doutorado
            </p>
            <p className="text-[1.75rem] leading-[1.15] font-bold tracking-tight text-balance sm:mt-5 sm:text-4xl xl:text-5xl">
              Orientação acadêmica com <span className="text-hero-accent">os prazos sob controle</span>
            </p>
            <p className="text-topbar-muted mt-3 max-w-lg text-base text-pretty sm:mt-4 lg:text-lg">
              Seus orientandos, as atividades de cada plano e o tempo até a qualificação e a defesa, em um só
              lugar.
            </p>
            <ul className="mt-8 hidden gap-x-8 gap-y-6 sm:grid sm:grid-cols-2">
              {HIGHLIGHTS.map(({ icon: Icon, title, text }) => (
                <li key={title} className="flex items-start gap-3">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20">
                    <Icon className="size-5" aria-hidden />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-semibold">{title}</span>
                    <span className="text-topbar-muted block text-sm">{text}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* A prévia só aparece em telas altas o bastante; nas baixas, a proposta e a lista já ocupam o painel. */}
          <div className="mt-10 hidden -rotate-1 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-6 motion-safe:duration-1000 lg:[@media(min-height:960px)]:block">
            <ProductPreview />
          </div>
        </div>

        <ul className="text-topbar-muted hidden flex-wrap gap-x-6 gap-y-2 text-sm lg:flex">
          {FACTS.map((f) => (
            <li key={f} className="flex items-center gap-2">
              <span aria-hidden className="size-1.5 rounded-full bg-white/60" />
              {f}
            </li>
          ))}
        </ul>
      </section>

      <div className="flex flex-1 flex-col px-6 sm:px-10">
        <main className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-10 sm:py-14">
          <div className="mb-7">
            <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
            {description && <p className="text-muted-foreground mt-2">{description}</p>}
          </div>
          {children}
        </main>
        <footer className="text-muted-foreground flex items-center justify-center gap-3 border-t py-5 text-xs lg:border-t-0">
          <span>© {new Date().getFullYear()} Orienta</span>
          <span aria-hidden>·</span>
          <Link to="/privacidade" className="hover:text-foreground underline-offset-4 hover:underline">
            Política de privacidade
          </Link>
        </footer>
      </div>
    </div>
  );
}
